import {AfterViewInit, Component, Inject, LOCALE_ID, OnInit} from '@angular/core';
import {AuthService} from '../../Services/auth.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {environment} from '../../../environments/environment';
import {StorageService} from '../../Services/storage.service';
import {SysUserModel} from '../../DataModels/sys-user-model';
import { Title } from '@angular/platform-browser';
import {BsToastService} from '../../Services/bs-toast-service';
import {Modal} from 'bootstrap';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: false
})


export class LoginComponent implements OnInit, AfterViewInit {
  version = environment.appVersion;
  idleForcedLogout = false;
  private otpModal: Modal | null = null;
  isOtpModalOpen = false;

  // lee = 'Hide Tide Software, Inc.';
  // leenum = 123.45;
  // leestr = '';
  // lee1 = $localize`:used on the main page|explains who created the app@@created_by:Created by ${this.lee}`;
  // currencyPipeString: string;

  loggingIn = false;
  submitted = false;
  testString: string;
  msgs = null;
  today: number = Date.now();

  public userInfo: SysUserModel;
  showPassword = false;

  // OTP UI state
  otpRequired = false;
  otpDelivery = '';
  otpMaskedTarget = '';
  otpInProgress = false;
  otpPrompt = false; // show link to start OTP, but do not auto-start

  loginForm: UntypedFormGroup;

  title = '';
  constructor(
    @Inject(LOCALE_ID) private locale: string,
    private authService: AuthService,
    private storage: StorageService,
    private formBuilder: UntypedFormBuilder,
    private notificationService: BsToastService,
    private titleService: Title,
    )
  {
    this.createForm();
  }

  createForm() {
    this.loginForm = this.formBuilder.group({
      username: ['app-user', Validators.required],
      password: ['', Validators.required],
      otp: [''],
    });
  }

  ngOnInit() {
    // Show forced-logout message if idle timeout triggered a logout
    this.idleForcedLogout = localStorage.getItem('idleForcedLogout') === '1';
    // Clear the flag so the message is only shown once per timeout event
    if (this.idleForcedLogout) {
      localStorage.removeItem('idleForcedLogout');
    }

    const tabTitle =  $localize`:accountXLogin: AccountX Login `;
    this.titleService.setTitle(tabTitle);

    // Subscribe to OTP state
    this.authService.otpRequired$.subscribe(v => {
      this.otpRequired = v;
      // If OTP is required and modal is not open, open it
      if (v && !this.isOtpModalOpen) {
        this.openOtpModal();
      }
    });
    this.authService.otpDelivery$.subscribe(v => this.otpDelivery = v);
    this.authService.otpMaskedTarget$.subscribe(v => this.otpMaskedTarget = v);
    this.authService.otpInProgress$.subscribe(v => {
      this.otpInProgress = v;
      if (v && !this.isOtpModalOpen) {
        this.openOtpModal();
      }
      if (!v && this.isOtpModalOpen) {
        this.closeOtpModal();
      }
    });
    this.authService.otpPrompt$.subscribe(v => {
      this.otpPrompt = v;
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
    });
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  onSubmit() {
    this.submitted = true;

    if (!this.otpRequired) {
      // Step 1: username/password
      if (this.loginForm.get('username').invalid || this.loginForm.get('password').invalid) {
        return;
      }
      this.loggingIn = true;
      try {
        this.authService.login(this.f.username.value, this.f.password.value).then((ok) => {
          this.loggingIn = false;
          // If ok is false but otpRequired flipped to true, UI will render OTP inputs or modal will open.
        });
      } catch (error) {
        // handle error
        this.loggingIn = false;
        //this.notificationService.showErrorToast(error);
        //console.error('Login failed: ', error);
      }
    } else {
      // Step 2: OTP verification (inline fallback if modal not used)
      const otpCtrl = this.loginForm.get('otp');
      if (!otpCtrl?.value) {
        otpCtrl?.setErrors({required: true});
        return;
      }
      if (otpCtrl.value?.toString().length !== 6) {
        otpCtrl?.setErrors({minlength: true});
        return;
      }
      this.authService.verifyOtp(this.f.username.value, otpCtrl.value).then((ok) => {
      });
    }
  }

  openOtpModal() {
    const el = document.getElementById('otpModal');
    if (el) {
      this.otpModal = Modal.getOrCreateInstance(el);
      this.otpModal.show();
      this.isOtpModalOpen = true;
      // Focus the first OTP box shortly after showing
      setTimeout(() => {
        this.focusOtpBox(0);
      }, 150);
    }
  }

  closeOtpModal() {
    if (this.otpModal) {
      this.otpModal.hide();
    }
    this.isOtpModalOpen = false;
    // Reset any validation state on modal-specific control if needed
  }

  verifyOtpFromModal() {
    const username = this.loginForm.get('username')?.value;
    if (!username) {
      return;
    }
    const code = this.getOtpValue();
    if (code.length !== 6) {
      this.notificationService.showWarningToast('Please enter the 6-character code.');
      return;
    }
    this.authService.verifyOtp(username, code).then((ok) => {
      if (ok) {
        this.closeOtpModal();
      }
    });
  }

  private getOtpBoxes(): HTMLInputElement[] {
    const inputs: HTMLInputElement[] = [];
    for (let i = 0; i < 6; i++) {
      const el = document.getElementById(`otpBox${i}`) as HTMLInputElement | null;
      if (el) {
        inputs.push(el);
      }
    }
    return inputs;
  }

  private clearOtpBoxes() {
    this.getOtpBoxes().forEach(i => i.value = '');
  }

  private focusOtpBox(index: number) {
    const el = document.getElementById(`otpBox${index}`) as HTMLInputElement | null;
    if (el) {
      el.focus();
      el.select?.();
    }
  }

  private getOtpValue(): string {
    return this.getOtpBoxes().map(i => (i.value || '').trim()).join('').slice(0, 6);
  }

  onOtpInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    let val = (input.value || '').replace(/\s+/g, '');
    if (val.length > 1) {
      // If user pasted multiple chars into one box, distribute via paste handler
      val = val[0];
    }
    // Allow only alphanumeric
    val = val.replace(/[^a-zA-Z0-9]/g, '');
    input.value = val;
    if (val && index < 5) {
      this.focusOtpBox(index + 1);
    }
  }

  onOtpKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const key = event.key;
    if (key === 'Backspace') {
      if (input.value) {
        // Clear this box and stay
        input.value = '';
        event.preventDefault();
        return;
      } else if (index > 0) {
        // Move to previous box and clear it
        event.preventDefault();
        this.focusOtpBox(index - 1);
        const prev = document.getElementById(`otpBox${index - 1}`) as HTMLInputElement;
        if (prev) {
          prev.value = '';
        }
        return;
      }
    }
    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusOtpBox(index - 1);
      return;
    }
    if (key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusOtpBox(index + 1);
      return;
    }
  }

  onOtpPaste(event: ClipboardEvent) {
    const data = event.clipboardData?.getData('text') || '';
    if (!data) {
      return;
    }
    event.preventDefault();
    const clean = data.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6);
    const boxes = this.getOtpBoxes();
    for (let i = 0; i < boxes.length; i++) {
      boxes[i].value = clean[i] || '';
    }
    // Focus last filled or next
    const lastIndex = Math.min(clean.length, 6) - 1;
    this.focusOtpBox(Math.max(0, Math.min(5, lastIndex + 1)));
  }

  startOtpFlow() {
    const username = this.loginForm.get('username')?.value;
    if (!username) {
      this.notificationService.showWarningToast('Please enter your username first.');
      return;
    }
    this.authService.sendOtp(username).then((sent) => {
      if (sent) {
        // Once OTP is sent, rely on otpInProgress subscription to open modal
        this.authService.otpPrompt$.next(false);
        if (!this.isOtpModalOpen) {
          this.openOtpModal();
        }
        setTimeout(() => {
          this.focusOtpBox(0);
        }, 150);
      }
    });
  }

  resendCode() {
    const username = this.loginForm.get('username')?.value;
    if (username) {
      this.authService.sendOtp(username).then(() => {
        // Keep modal open and refocus input
        if (this.isOtpModalOpen) {
          setTimeout(() => {
            const input = document.getElementById('otpInput') as HTMLInputElement;
            input?.focus();
          }, 150);
        }
      });
    }
  }
}
